import { apiSlice } from '../../api';
import type {
  CreateTransitAddressDTO,
  CreateTransitAddressResult,
  CreateTransitAddressWithdrawDTO,
  CreateTransitAddressWithdrawResult,
  EstimateTransactionFeeDTO,
  EstimateTransactionFeeResult,
  GetBlockchainBalanceDTO,
  GetBlockchainBalanceResult,
  GetTransitAddressByIdDTO,
  GetTransitAddressByIdResult,
  GetTransitAddressesDTO,
  GetTransitAddressesResult,
  GetTransitAddressesWithdrawaAddresseslDTO,
  GetTransitAddressesWithdrawalAddressesResult,
  GetTransitAddressesWithdrawalsDTO,
  GetTransitAddressesWithdrawalsResult,
} from './types';

const adminTransitAddressesEndpoints = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getTransitAddresses: build.query<GetTransitAddressesResult, GetTransitAddressesDTO>({
      query: () => ({
        url: '/admin/v1/transit-addresses',
        method: 'GET',
      }),
      providesTags: ['TransitAddresses'],
    }),
    getTransitAddressById: build.query<GetTransitAddressByIdResult, GetTransitAddressByIdDTO>({
      query: ({ addressId }) => ({
        url: `/admin/v1/transit-addresses/${addressId}`,
        method: 'GET',
      }),
      providesTags: ['TransitAddresses'],
    }),

    getTransitAddressesWithdrawal: build.query<
      GetTransitAddressesWithdrawalsResult,
      GetTransitAddressesWithdrawalsDTO
    >({
      query: ({ addressId }) => ({
        url: `/admin/v1/transit-addresses/${addressId}/withdrawals`,
        method: 'GET',
      }),
      providesTags: ['TransitAddresses'],
    }),

    getTransitAddressesWithdrawalAddresses: build.query<
      GetTransitAddressesWithdrawalAddressesResult,
      GetTransitAddressesWithdrawaAddresseslDTO
    >({
      query: () => ({
        url: '/admin/v1/transit-addresses/withdraw-addresses',
        method: 'GET',
      }),
      providesTags: ['TransitAddresses'],
    }),

    createTransitAddressWithdraw: build.mutation<
      CreateTransitAddressWithdrawResult,
      CreateTransitAddressWithdrawDTO
    >({
      query: ({ body, coin_id }) => ({
        url: `/admin/v1/transit-addresses/${coin_id}/withdraw`,
        method: 'POST',
        body,
        headers: {
          ['confirm-action-code']: body.password,
        },
      }),
      invalidatesTags: ['TransitAddresses'],
    }),

    createTransitAddress: build.mutation<CreateTransitAddressResult, CreateTransitAddressDTO>({
      query: ({ body }) => ({
        url: '/admin/v1/transit-addresses',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TransitAddresses'],
    }),

    estimateTransactionFee: build.query<EstimateTransactionFeeResult, EstimateTransactionFeeDTO>({
      query: ({ addressId, body }) => ({
        url: `/admin/v1/transit-addresses/${addressId}/estimate-fee`,
        method: 'POST',
        body,
      }),
    }),

    getBlockchainBalance: build.query<GetBlockchainBalanceResult, GetBlockchainBalanceDTO>({
      query: ({ addressId }) => ({
        url: `/admin/v1/transit-addresses/${addressId}/blockchain-balance`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetTransitAddressesQuery,
  useLazyGetTransitAddressesWithdrawalQuery,
  useGetTransitAddressesWithdrawalQuery,
  useCreateTransitAddressMutation,
  useLazyEstimateTransactionFeeQuery,
  useLazyGetBlockchainBalanceQuery,
  useGetTransitAddressesWithdrawalAddressesQuery,
  useCreateTransitAddressWithdrawMutation,
  useGetBlockchainBalanceQuery,
  useGetTransitAddressByIdQuery,
  useLazyGetTransitAddressByIdQuery,
  useLazyGetTransitAddressesQuery,
  useLazyGetTransitAddressesWithdrawalAddressesQuery,
} = adminTransitAddressesEndpoints;

export type AdminTransitAddressesController = typeof adminTransitAddressesEndpoints;
